/**
 * Revenue against cost: the COGS dashboard.
 *
 * Every amount keeps its own currency. Where figures in different currencies
 * are combined, the server combines them in a display currency the viewer
 * picks, converting each line at its own date from the ECB's daily reference
 * rates; each converted figure says which rate and publication day it used.
 * A line with no recent enough rate is counted as unconverted and left out of
 * the sums rather than guessed; a cost line with no amount at all is unrated.
 * Neither is ever shown as zero.
 */

function toNumber(value: string | number): number {
  return typeof value === 'number' ? value : Number.parseFloat(value);
}

export const DISPLAY_CURRENCIES = ['GBP', 'USD', 'EUR'] as const;
export type DisplayCurrency = (typeof DISPLAY_CURRENCIES)[number];

export type CostCategory = 'partner' | 'qa' | 'llm' | 'external_api' | 'registry' | 'monitoring';

/** Fixed order: a category keeps its place (and its colour) whatever the filter. */
export const COST_CATEGORIES: readonly CostCategory[] = [
  'partner',
  'qa',
  'llm',
  'external_api',
  'registry',
  'monitoring',
];

export const COST_CATEGORY_LABELS: Readonly<Record<CostCategory, string>> = {
  partner: 'Partner fees',
  qa: 'QA',
  llm: 'LLM',
  external_api: 'Data providers',
  registry: 'Registry documents',
  monitoring: 'Monitoring',
};

export type CostSource =
  | 'hub_assignment'
  | 'usage_event'
  | 'pool_remainder'
  | 'monitoring'
  | 'backoffice_np_request'
  | 'backoffice_qa'
  | 'backoffice_partner_price';

export type CostBasis = 'actual' | 'quoted' | 'rated';

export const COST_BASIS_LABELS: Readonly<Record<CostBasis, string>> = {
  actual: 'Charged',
  quoted: 'Quoted',
  rated: 'From rate card',
};

export type ConsumptionSource = 'hub_report' | 'backoffice_project';

// --- Money ---

export interface Converted {
  amount: number;
  currency: DisplayCurrency;
  /** Units of the display currency per unit of the original. */
  rate: number;
  /** The publication used; earlier than the line's own date on weekends. */
  rateDate: string;
  source: string;
}

export interface Money {
  amount: number;
  currency: string;
  /** Null when no rate was published within a week before the line's date. */
  converted: Converted | null;
}

export interface ApiConverted {
  amount: string | number;
  currency: DisplayCurrency;
  rate: string | number;
  rate_date: string;
  source: string;
}

export interface ApiMoney {
  amount: string | number;
  currency: string;
  converted: ApiConverted | null;
}

export function transformToMoney(api: ApiMoney | null): Money | null {
  if (!api) {
    return null;
  }
  return {
    amount: toNumber(api.amount),
    currency: api.currency,
    converted: api.converted
      ? {
          amount: toNumber(api.converted.amount),
          currency: api.converted.currency,
          rate: toNumber(api.converted.rate),
          rateDate: api.converted.rate_date,
          source: api.converted.source,
        }
      : null,
  };
}

/** Revenue, cost and margin combined in the display currency. */
export interface Totals {
  currency: DisplayCurrency;
  revenue: number;
  cost: number;
  margin: number;
  costByCategory: Partial<Record<CostCategory, number>>;
  /** Lines left out of the sums because no rate could convert them. */
  unconverted: number;
  /** Cost lines with no amount at all. */
  unrated: number;
}

export interface ApiTotals {
  currency: DisplayCurrency;
  revenue: string | number;
  cost: string | number;
  margin: string | number;
  cost_by_category: Partial<Record<CostCategory, string | number>>;
  unconverted: number;
  unrated: number;
}

export function transformToTotals(api: ApiTotals): Totals {
  return {
    currency: api.currency,
    revenue: toNumber(api.revenue),
    cost: toNumber(api.cost),
    margin: toNumber(api.margin),
    costByCategory: Object.fromEntries(
      Object.entries(api.cost_by_category ?? {}).map(([k, v]) => [k, toNumber(v as string)])
    ),
    unconverted: api.unconverted,
    unrated: api.unrated,
  };
}

/** Margin as a share of revenue; null when there is no revenue to divide by. */
export function marginShare(totals: Totals): number | null {
  return totals.revenue > 0 ? totals.margin / totals.revenue : null;
}

/** Whether the totals leave lines out — the figures are then a floor. */
export function isIncomplete(totals: Totals): boolean {
  return totals.unconverted > 0 || totals.unrated > 0;
}

// --- Projects ---

export interface ProjectEconomics {
  source: ConsumptionSource;
  sourceRef: string;
  reportId: string | null;
  backofficeProjectId: string | null;
  projectCode: string | null;
  teamId: string;
  label: string | null;
  consumedAt: string;
  /** The price as billed, in its own currency; null when unpriced. */
  revenue: Money | null;
  voided: boolean;
  totals: Totals;
}

export interface ApiProjectEconomics {
  source: ConsumptionSource;
  source_ref: string;
  report_id: string | null;
  backoffice_project_id: string | null;
  project_code: string | null;
  team_id: string;
  label: string | null;
  consumed_at: string;
  revenue: ApiMoney | null;
  voided: boolean;
  totals: ApiTotals;
}

export function transformToProjectEconomics(api: ApiProjectEconomics): ProjectEconomics {
  return {
    source: api.source,
    sourceRef: api.source_ref,
    reportId: api.report_id,
    backofficeProjectId: api.backoffice_project_id,
    projectCode: api.project_code,
    teamId: api.team_id,
    label: api.label,
    consumedAt: api.consumed_at,
    revenue: transformToMoney(api.revenue),
    voided: api.voided,
    totals: transformToTotals(api.totals),
  };
}

export interface ProjectEconomicsPage {
  items: ProjectEconomics[];
  total: number;
  page: number;
  pageSize: number;
}

export interface ApiProjectEconomicsPage {
  items: ApiProjectEconomics[];
  total: number;
  page: number;
  page_size: number;
}

export function transformToProjectEconomicsPage(
  api: ApiProjectEconomicsPage
): ProjectEconomicsPage {
  return {
    items: api.items.map(transformToProjectEconomics),
    total: api.total,
    page: api.page,
    pageSize: api.page_size,
  };
}

export interface CostLine {
  id: string;
  source: CostSource;
  sourceRef: string;
  category: CostCategory;
  provider: string | null;
  service: string | null;
  reportId: string | null;
  backofficeProjectId: string | null;
  teamId: string | null;
  costDate: string;
  quantity: Record<string, unknown> | null;
  /** Null when unrated. */
  money: Money | null;
  basis: CostBasis | null;
  unratedReason: string | null;
  providerRateId: string | null;
  label: string | null;
  detail: Record<string, unknown> | null;
  voidedAt: string | null;
  voidReason: string | null;
}

export interface ApiCostLine {
  id: string;
  source: CostSource;
  source_ref: string;
  category: CostCategory;
  provider: string | null;
  service: string | null;
  report_id: string | null;
  backoffice_project_id: string | null;
  team_id: string | null;
  cost_date: string;
  quantity: Record<string, unknown> | null;
  money: ApiMoney | null;
  basis: CostBasis | null;
  unrated_reason: string | null;
  provider_rate_id: string | null;
  label: string | null;
  detail: Record<string, unknown> | null;
  voided_at: string | null;
  void_reason: string | null;
}

export function transformToCostLine(api: ApiCostLine): CostLine {
  return {
    id: api.id,
    source: api.source,
    sourceRef: api.source_ref,
    category: api.category,
    provider: api.provider,
    service: api.service,
    reportId: api.report_id,
    backofficeProjectId: api.backoffice_project_id,
    teamId: api.team_id,
    costDate: api.cost_date,
    quantity: api.quantity,
    money: transformToMoney(api.money),
    basis: api.basis,
    unratedReason: api.unrated_reason,
    providerRateId: api.provider_rate_id,
    label: api.label,
    detail: api.detail,
    voidedAt: api.voided_at,
    voidReason: api.void_reason,
  };
}

/** Where a registry order's share of its assignment's fee came from. */
export type RegistryOrderFeeSource =
  | 'list_price'
  | 'quote'
  | 'awaiting_quote'
  | 'not_gbp'
  | 'not_billable';

export const REGISTRY_FEE_SOURCE_LABELS: Readonly<Record<RegistryOrderFeeSource, string>> = {
  list_price: 'Catalogue price',
  quote: 'Quoted',
  awaiting_quote: 'Awaiting quote',
  not_gbp: 'Not in GBP — not billed',
  not_billable: 'Not billed',
};

export interface RegistryOrderFee {
  orderId: string;
  providerOrderId: string | null;
  companyName: string;
  products: string[];
  status: string;
  /** GBP, to the penny. */
  fee: number;
  feeSource: RegistryOrderFeeSource;
}

/** The Armadillo orders a partner fee pays for. ``feeFrom`` says whether the
 * fee is the orders' total (plus any adjustment) or a figure an analyst set,
 * which need not equal the orders' total. */
export interface RegistryOrderBreakdown {
  feeFrom: 'orders' | 'analyst';
  orders: RegistryOrderFee[];
  adjustment: { amount: number; note: string | null } | null;
}

interface ApiRegistryOrderBreakdown {
  fee_from: 'orders' | 'analyst';
  orders: {
    order_id: string;
    provider_order_id: string | null;
    company_name: string;
    products: string[];
    status: string;
    fee: string;
    fee_source: RegistryOrderFeeSource;
  }[];
  fee_adjustment: { amount: string; note: string | null } | null;
}

/** A partner fee line's registry-order breakdown, or null when it bills none. */
export function registryOrderBreakdown(line: CostLine): RegistryOrderBreakdown | null {
  const raw = line.detail?.registry_orders as ApiRegistryOrderBreakdown | null | undefined;
  if (!raw || !Array.isArray(raw.orders)) {
    return null;
  }
  return {
    feeFrom: raw.fee_from,
    orders: raw.orders.map((order) => ({
      orderId: order.order_id,
      providerOrderId: order.provider_order_id,
      companyName: order.company_name,
      products: order.products ?? [],
      status: order.status,
      fee: Number(order.fee),
      feeSource: order.fee_source,
    })),
    adjustment: raw.fee_adjustment
      ? { amount: Number(raw.fee_adjustment.amount), note: raw.fee_adjustment.note }
      : null,
  };
}

export interface ProjectEconomicsDetail extends ProjectEconomics {
  clientReference: string | null;
  description: string | null;
  /** Every cost line behind the work, voided ones included (never summed). */
  costs: CostLine[];
}

export interface ApiProjectEconomicsDetail extends ApiProjectEconomics {
  client_reference: string | null;
  description: string | null;
  costs: ApiCostLine[];
}

export function transformToProjectEconomicsDetail(
  api: ApiProjectEconomicsDetail
): ProjectEconomicsDetail {
  return {
    ...transformToProjectEconomics(api),
    clientReference: api.client_reference,
    description: api.description,
    costs: api.costs.map(transformToCostLine),
  };
}

// --- Summary ---

export interface MonthEconomics {
  /** YYYY-MM */
  month: string;
  totals: Totals;
}

export interface ProviderCost {
  /** A provider, or a category name for costs with none (partner fees). */
  provider: string;
  cost: number;
  lines: number;
  unrated: number;
}

export interface TeamEconomics {
  teamId: string;
  totals: Totals;
}

export interface Coverage {
  /** Completed Hub reports in the period, and how many recorded any usage —
   * reports from before usage was captured have none. */
  completedReports: number;
  reportsWithUsage: number;
  unratedLines: number;
  unratedReasons: { reason: string; lines: number }[];
}

export interface EconomicsSummary {
  periodFrom: string;
  periodTo: string;
  totals: Totals;
  byMonth: MonthEconomics[];
  byProvider: ProviderCost[];
  byTeam: TeamEconomics[];
  coverage: Coverage;
}

export interface ApiEconomicsSummary {
  period_from: string;
  period_to: string;
  totals: ApiTotals;
  by_month: { month: string; totals: ApiTotals }[];
  by_provider: { provider: string; cost: string | number; lines: number; unrated: number }[];
  by_team: { team_id: string; totals: ApiTotals }[];
  coverage: {
    completed_reports: number;
    reports_with_usage: number;
    unrated_lines: number;
    unrated_reasons: { reason: string; lines: number }[];
  };
}

export function transformToEconomicsSummary(api: ApiEconomicsSummary): EconomicsSummary {
  return {
    periodFrom: api.period_from,
    periodTo: api.period_to,
    totals: transformToTotals(api.totals),
    byMonth: api.by_month.map((m) => ({ month: m.month, totals: transformToTotals(m.totals) })),
    byProvider: api.by_provider.map((p) => ({
      provider: p.provider,
      cost: toNumber(p.cost),
      lines: p.lines,
      unrated: p.unrated,
    })),
    byTeam: api.by_team.map((t) => ({ teamId: t.team_id, totals: transformToTotals(t.totals) })),
    coverage: {
      completedReports: api.coverage.completed_reports,
      reportsWithUsage: api.coverage.reports_with_usage,
      unratedLines: api.coverage.unrated_lines,
      unratedReasons: api.coverage.unrated_reasons,
    },
  };
}

/** Share of completed reports with any usage recorded; null with none completed. */
export function usageCoverage(coverage: Coverage): number | null {
  return coverage.completedReports > 0
    ? coverage.reportsWithUsage / coverage.completedReports
    : null;
}

export interface UnattributedCosts {
  lines: CostLine[];
  totals: Totals;
}

export interface ApiUnattributedCosts {
  lines: ApiCostLine[];
  totals: ApiTotals;
}

export function transformToUnattributedCosts(api: ApiUnattributedCosts): UnattributedCosts {
  return { lines: api.lines.map(transformToCostLine), totals: transformToTotals(api.totals) };
}

// --- Display currency preference ---

const DISPLAY_CURRENCY_KEY = 'economics.displayCurrency';

/** The viewer's chosen display currency, remembered per browser. Storage can
 * be unavailable (private windows, blocked site data); GBP is then used. */
export function readDisplayCurrency(): DisplayCurrency {
  try {
    const stored = window.localStorage.getItem(DISPLAY_CURRENCY_KEY);
    return (DISPLAY_CURRENCIES as readonly string[]).includes(stored ?? '')
      ? (stored as DisplayCurrency)
      : 'GBP';
  } catch {
    return 'GBP';
  }
}

export function writeDisplayCurrency(currency: DisplayCurrency): void {
  try {
    window.localStorage.setItem(DISPLAY_CURRENCY_KEY, currency);
  } catch {
    // A preference that cannot be stored is only lost on reload.
  }
}

// --- Refresh ---

export interface SweepCounts {
  inserted: number;
  updated: number;
  voided: number;
  restored: number;
  removed: number;
}

export interface EconomicsRefreshResult {
  sweeps: Record<string, SweepCounts>;
  /** Set when Backoffice could not be read: contracts, consumption and
   * Backoffice's own costs were left as they were. */
  backofficeError: string | null;
}

export interface ApiEconomicsRefreshResult {
  sweeps: Record<string, SweepCounts>;
  backoffice_error: string | null;
}

export function transformToEconomicsRefreshResult(
  api: ApiEconomicsRefreshResult
): EconomicsRefreshResult {
  return { sweeps: api.sweeps, backofficeError: api.backoffice_error };
}

/** The filters every economics read takes. Dates are ISO days, inclusive. */
export interface EconomicsQuery {
  displayCurrency: DisplayCurrency;
  teamId?: string;
  periodFrom?: string;
  periodTo?: string;
}

export interface ProjectEconomicsQuery extends EconomicsQuery {
  source?: ConsumptionSource;
  reportConfigId?: string;
  includeVoided?: boolean;
  page: number;
  pageSize: number;
}
